import { Link } from 'react-router-dom'
import styles from './Home.module.scss'
import { useUser, useUpcomingToursWithStatus } from '../../hooks'
import { usePreContractList } from '../../hooks/offer'
import { useToken } from '../../utils'
import { AcceptedPreContractsSection } from '../precontractmanagement/AcceptedPreContractsSection'
import { HomeStatsCards } from './components/HomeStatsCards'
import { HomeScheduleCalendar } from './components/HomeScheduleCalendar'
import { HomeContractedSchedule } from './components/HomeContractedSchedule'

const formatTourDate = (startedAt, endedAt) => {
	if (!startedAt || !endedAt) return ''
	const s =
		typeof startedAt === 'string'
			? startedAt.slice(0, 10)
			: new Date(startedAt).toISOString().slice(0, 10)
	const e =
		typeof endedAt === 'string'
			? endedAt.slice(0, 10)
			: new Date(endedAt).toISOString().slice(0, 10)
	return `${s} ~ ${e}`
}

export const Home = ({ ...props }) => {
	const { data: user } = useUser()
	const token = useToken()
	const { data: preContractData } = usePreContractList(token)
	const hasAcceptedPreContracts = (preContractData?.data || []).some(
		(item) => item?.pre_contract_status === 'ACCEPTED',
	)
	const { data: upcomingTours = [], isLoading: upcomingLoading } = useUpcomingToursWithStatus(10)
	const toursList = Array.isArray(upcomingTours) ? upcomingTours : []

	return (
		<div className={styles.homeviewWrapper}>
			<div className={styles.announcementBanner}>
				<div className={styles.bannerContent}>
					<div className={styles.bannerText}>
						<span className={styles.bannerTitle}>2026년 정식 계약이 오픈했습니다!</span>
						<span className={styles.bannerSubtitle}>
							사전계약을 한 고객님들은 정식계약으로 반드시 전환해 주세요.
						</span>
					</div>
				</div>
			</div>
			{/* 사전계약 현황: 체결된 사전계약이 있을 때만 표시 */}
			{hasAcceptedPreContracts && (
				<div className={styles.preContractSection}>
					<div className={styles.titleBox}>
						<span className={styles.boxLabel}>사전계약 현황</span>
					</div>
					<AcceptedPreContractsSection variant="home" showTitle={false} />
				</div>
			)}
			<div className={styles.upperBox}>
				<div className={styles.titleBox}>
					<span className={styles.boxLabel}>홈</span>
					<span className={styles.subtitle}>내 일정을 관리해보세요!</span>
				</div>
			</div>
			<HomeStatsCards />
			<HomeScheduleCalendar />
			<HomeContractedSchedule />
			<div className={styles.upcomingWrapper}>
				<div className={styles.upcomingTitle}>
					<h2 className={styles.boxLabel}>다가오는 대회</h2>
					{user?.userType === 'tourPro' && (
						<Link
							to="/myinfo/upcomingtournaments"
							className={styles.upcomingSettingLink}>
							출전예정 대회설정하기 &gt;
						</Link>
					)}
				</div>
				{upcomingLoading ? (
					<div className={styles.upcomingEmptyState}>
						<div className={styles.upcomingEmptyDescription}>로딩 중...</div>
					</div>
				) : toursList.length > 0 ? (
					<ul className={styles.upcomingTourList}>
						{toursList.map((t) => {
							// 캐디는 출전 설정이 없으므로 라벨을 '프로 찾기' 또는 '계약 완료'만 표시
							const isCaddie = user?.userType !== 'tourPro'
							const isInternational = t.region === 'INTERNATIONAL'
							const primaryTag = isInternational ? '해외' : t.type
							const secondaryTag = isInternational ? t.tourCategory : null
							const displayLabel = isCaddie
								? t.label === '계약 완료'
									? '계약 완료'
									: '프로 찾기'
								: t.label
							const isFind =
								displayLabel === '캐디 찾기' || displayLabel === '프로 찾기'
							const findPath = '/search'
							const labelClass =
								displayLabel === '계약 완료'
									? styles.upcomingTourLabelDone
									: displayLabel === '미출전'
										? styles.upcomingTourLabelMuted
										: styles.upcomingTourLabelFind
							const content = (
								<>
									{displayLabel === '계약 완료' && (
										<svg
											className={styles.upcomingTourLabelCheck}
											width="12"
											height="12"
											viewBox="0 0 12 12"
											fill="none"
											aria-hidden="true">
											<path
												d="M10 3L4.5 8.5L2 6"
												stroke="currentColor"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									)}
									{displayLabel}
									{isFind && (
										<span
											className={styles.upcomingTourLabelArrow}
											aria-hidden="true">
											&gt;
										</span>
									)}
								</>
							)
							return (
								<li key={t.id} className={styles.upcomingTourItem}>
									<div className={styles.upcomingTourContent}>
										{(primaryTag || secondaryTag) && (
											<div className={styles.upcomingTourMetaTags}>
												{primaryTag && (
													<span className={styles.upcomingTourTag}>
														{primaryTag}
													</span>
												)}
												{secondaryTag && (
													<span
														className={`${styles.upcomingTourTag} ${styles.upcomingTourTagInternational}`}>
														{secondaryTag}
													</span>
												)}
											</div>
										)}
										<div className={styles.upcomingTourName}>{t.name}</div>
										<div className={styles.upcomingTourMeta}>
											{formatTourDate(t.started_at, t.ended_at)}
										</div>
									</div>
									{displayLabel &&
										(isFind ? (
											<Link
												to={
													displayLabel === '프로 찾기'
														? `/search?tab=partner&tourId=${encodeURIComponent(t.id)}&tourType=${encodeURIComponent(t.type || '')}`
														: findPath
												}
												className={`${styles.upcomingTourLabel} ${labelClass} ${styles.upcomingTourLabelLink}`}>
												{content}
											</Link>
										) : (
											<span
												className={`${styles.upcomingTourLabel} ${labelClass}`}>
												{content}
											</span>
										))}
								</li>
							)
						})}
					</ul>
				) : (
					<div className={styles.upcomingEmptyState}>
						<div className={styles.upcomingEmptyTitle}>아직 등록된 대회가 없습니다</div>
						<div className={styles.upcomingEmptyDescription}>
							아직 대회 정보가 공개되지 않았어요.
							<br />곧 다가오는 대회 정보를 확인할 수 있도록 준비 중입니다.
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
